import api from "@/plugins/api";
import { appendQuery } from "@/shared/utils/url";
const BASE_PATH = "templates";
/**
 * API client for template operations.
 * All calls go through the BFF (unwrapEnvelope: true).
 */
export const templatesApi = {
    /**
     * Gets a paginated list of templates with optional filters.
     */
    async getTemplates(params = {}) {
        const url = appendQuery(BASE_PATH, {
            page: params.page,
            pageSize: params.pageSize,
            support: params.support,
            status: params.status,
            searchTerm: params.searchTerm,
            language: params.language,
        });
        const response = await api.get(url, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets the distinct languages (BCP-47 tags) of the non-archived templates.
     */
    async getTemplateLanguages() {
        const response = await api.get(`${BASE_PATH}/languages`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Duplicates an existing template.
     */
    async duplicateTemplate(id) {
        const response = await api.post(`${BASE_PATH}/${id}/duplicate`, {}, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Deletes a template by ID.
     */
    async deleteTemplate(id) {
        await api.delete(`${BASE_PATH}/${id}`, { unwrapEnvelope: true });
    },
    /**
     * Creates a new template.
     */
    async createTemplate(data) {
        const response = await api.post(BASE_PATH, data, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Gets a template by ID.
     */
    async getTemplateById(id) {
        const response = await api.get(`${BASE_PATH}/${id}`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Updates a template's design (Unlayer JSON + HTML).
     */
    async updateTemplateDesign(id, data) {
        const response = await api.put(`${BASE_PATH}/${id}/design`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Updates a template's metadata (name, status, etc.).
     */
    async updateTemplate(id, data) {
        const response = await api.put(`${BASE_PATH}/${id}`, data, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Renames a template.
     */
    async renameTemplate(id, data) {
        const response = await api.put(`${BASE_PATH}/${id}/rename`, data, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Sends a test email for a template using sample merge-variable values.
     */
    async sendTestEmail(id, data) {
        await api.post(`${BASE_PATH}/${id}/send-test`, data, {
            unwrapEnvelope: true,
        });
    },
    /** Publishes a template (sets published_version_id to latest version). */
    async publishTemplate(id) {
        const response = await api.post(`${BASE_PATH}/${id}/publish`, {}, { unwrapEnvelope: true });
        return response.data;
    },
    /** Gets a single template version by ID, including design content. */
    async getTemplateVersionById(templateId, versionId) {
        const response = await api.get(`${BASE_PATH}/${templateId}/versions/${versionId}`, { unwrapEnvelope: true });
        return response.data;
    },
    /** Gets the version history of a template. */
    async getTemplateVersions(id, page = 1, pageSize = 20) {
        const url = appendQuery(`${BASE_PATH}/${id}/versions`, { page, pageSize });
        const response = await api.get(url, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /** Restores a previous version of a template. */
    async restoreVersion(id, versionId) {
        const response = await api.post(`${BASE_PATH}/${id}/versions/${versionId}/restore`, {}, { unwrapEnvelope: true });
        return response.data;
    },
};
